import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import UserIcon from 'icons/User.icon';
import { useTranslation } from 'react-i18next';
import TimelineNode from 'components/TimelineNode';
import { CollapsibleSection } from 'components/CollapsibleSection';
import {
  DATA_ROOM_NDA_SIGNING_STATUS,
  DATA_ROOM_NDA_SIGNING_TIMELINE_ACTION,
  DataRoomNdaSigningTimeline,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import { NDA_SIGNING_STATUS_COPY, NDA_SIGNING_TRACK_EVENTS_COPY } from './data';
import useGenerateTimelineActions from './hooks';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import TransWithNda from 'components/TransWithNda';

import './DataRoomNdaTimeline.styles.scss';

type DataRoomNdaTimelineProps = {
  className?: string;
  signingId: string;
  firstName: string;
  numberOfNotSigned: number;
  timeline: DataRoomNdaSigningTimeline[];
  envelopeTemplateName: string;
  onSendReminder: (signingId: string) => void;
};

const DataRoomNdaTimeline: React.FC<DataRoomNdaTimelineProps> = (props) => {
  const {
    className,
    signingId,
    firstName,
    numberOfNotSigned,
    envelopeTemplateName,
    timeline = [],
    onSendReminder,
  } = props;

  const {
    currentUser: { email: currentUserEmail },
  } = useContext(CurrentUserContext);

  const classes = classNames(
    'data-room-nda-timeline',
    'white-panel',
    className,
  );

  const lastElement = timeline?.length - 1;

  const { t } = useTranslation();

  const ndaTimelineActions = useGenerateTimelineActions(
    signingId,
    onSendReminder,
  );

  const reversedTimeline = useMemo(
    () =>
      timeline
        .map(
          (
            {
              action,
              date,
              name,
              email,
              rejectionReason,
              previousStatus,
              status,
            },
            index,
          ) => (
            <TimelineNode
              key={action + email}
              date={date}
              text={
                <TransWithNda
                  i18nKey={
                    action ===
                      DATA_ROOM_NDA_SIGNING_TIMELINE_ACTION.MANUALLY_UPDATED &&
                    !previousStatus
                      ? 'NDAmanuallyUpdatedTo'
                      : NDA_SIGNING_TRACK_EVENTS_COPY[action]
                  }
                  envelopeTemplateName={envelopeTemplateName}
                  values={{
                    email,
                    rejectionReason,
                    name: currentUserEmail === email ? t('you') : name,
                    previousStatus: t(NDA_SIGNING_STATUS_COPY[previousStatus]),
                    status: t(NDA_SIGNING_STATUS_COPY[status]),
                  }}
                />
              }
            >
              {index === lastElement &&
                (action !==
                  DATA_ROOM_NDA_SIGNING_TIMELINE_ACTION.MANUALLY_UPDATED ||
                  status === DATA_ROOM_NDA_SIGNING_STATUS.PENDING) &&
                ndaTimelineActions[action]}
            </TimelineNode>
          ),
        )
        .reverse(),
    [
      currentUserEmail,
      ndaTimelineActions,
      lastElement,
      timeline,
      envelopeTemplateName,
      t,
    ],
  );

  return (
    <CollapsibleSection
      className={classes}
      defaultOpen
      trigger={
        <p className="heading">
          <UserIcon />
          {t('userNDATimeline', { firstName })}
        </p>
      }
    >
      <div className="content">
        {reversedTimeline}

        {!!numberOfNotSigned && (
          <p>{t('peopleWaitingToSign', { count: numberOfNotSigned })}</p>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default DataRoomNdaTimeline;
